'use client'

import { useEffect, useState } from 'react'
import {
    MdSave,
    MdSettings,
    MdToggleOn,
    MdToggleOff,
    MdAutoAwesome,
    MdTrendingDown,
    MdSentimentSatisfied,
    MdAccountTree,
    MdLock,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import StorageRolePicker from '@/pages/admin/core/storage/storage-role-picker'
import {
    getSalesConfig,
    updateSalesConfig,
    SalesPipelineConfig,
    UpdateSalesConfigData,
} from '@/lib/api/sales'

export default function SalesConfigPage() {
    const { user } = useAuth()
    const { canUpdate } = useResourcePermissions('sales', 'config')
    // Definir quem vê todos os funis também exige edição de funis (o backend confere).
    const { canUpdate: canUpdatePipelines } = useResourcePermissions('sales', 'pipelines')

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [config, setConfig] = useState<SalesPipelineConfig | null>(null)

    // Form state
    const [allowMultiplePipelines, setAllowMultiplePipelines] = useState(false)
    const [allowLeadInMultiplePipelines, setAllowLeadInMultiplePipelines] = useState(false)
    const [autoAssignLeadWallet, setAutoAssignLeadWallet] = useState(false)
    const [autoAssignDealWallet, setAutoAssignDealWallet] = useState(false)
    const [sentimentAnalysisEnabled, setSentimentAnalysisEnabled] = useState(false)
    const [scoreDecayEnabled, setScoreDecayEnabled] = useState(false)
    const [scoreDecayDays, setScoreDecayDays] = useState(30)
    const [viewAllPipelinesRoleIds, setViewAllPipelinesRoleIds] = useState<number[]>([])

    useEffect(() => {
        if (user?.companyId) {
            loadConfig()
        }
    }, [user?.companyId])

    const loadConfig = async () => {
        setLoading(true)
        try {
            const response = await getSalesConfig()
            if (response.success && response.result) {
                const c = response.result
                setConfig(c)
                setAllowMultiplePipelines(c.allowMultiplePipelines)
                setAllowLeadInMultiplePipelines(c.allowLeadInMultiplePipelines)
                setAutoAssignLeadWallet(c.autoAssignLeadWallet)
                setAutoAssignDealWallet(c.autoAssignDealWallet)
                setSentimentAnalysisEnabled(c.sentimentAnalysisEnabled)
                setScoreDecayEnabled(c.scoreDecayEnabled)
                setScoreDecayDays(c.scoreDecayDays || 30)
                setViewAllPipelinesRoleIds(c.viewAllPipelinesRoleIds ?? [])
            }
        } catch {
            toast.error('Erro ao carregar configurações de vendas')
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            const data: UpdateSalesConfigData = {
                allowMultiplePipelines,
                allowLeadInMultiplePipelines,
                autoAssignLeadWallet,
                autoAssignDealWallet,
                sentimentAnalysisEnabled,
                scoreDecayEnabled,
                scoreDecayDays: scoreDecayEnabled ? scoreDecayDays : undefined,
            }

            // Só envia os perfis quando mudaram: quem não edita funis ainda salva o resto.
            const savedRoleIds = [...(config?.viewAllPipelinesRoleIds ?? [])].sort((a, b) => a - b)
            const currentRoleIds = [...viewAllPipelinesRoleIds].sort((a, b) => a - b)
            if (savedRoleIds.join(',') !== currentRoleIds.join(',')) {
                data.viewAllPipelinesRoleIds = viewAllPipelinesRoleIds
            }

            const response = await updateSalesConfig(data)
            if (response.success) {
                setConfig(response.result)
                toast.success('Configurações salvas com sucesso')
            } else {
                toast.error(response.error || 'Erro ao salvar configurações')
            }
        } catch {
            toast.error('Erro ao salvar configurações')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col gap-6 max-w-4xl mx-auto p-6">
                <div className="flex items-center gap-3">
                    <div className="skeleton w-10 h-10 rounded-xl" />
                    <div>
                        <div className="skeleton h-6 w-60 mb-1" />
                        <div className="skeleton h-4 w-40" />
                    </div>
                </div>
                {[1, 2, 3].map(i => (
                    <div key={i} className="card bg-base-100 shadow-sm">
                        <div className="card-body">
                            <div className="skeleton h-5 w-48 mb-4" />
                            <div className="skeleton h-4 w-full mb-2" />
                            <div className="skeleton h-10 w-full" />
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdSettings className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Configurações de Vendas
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Ajuste as preferências do módulo de vendas
                        </p>
                    </div>
                </div>
                {canUpdate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdSave className="text-lg" />
                        )}
                        Salvar
                    </button>
                )}
            </div>

            {/* Funis */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body">
                    <div className="flex items-center gap-2 mb-4">
                        <MdAccountTree className="text-xl text-secondary" />
                        <h2 className="card-title text-lg">Funis de Vendas</h2>
                    </div>

                    <div className="space-y-5">
                        <ToggleRow
                            label="Permitir múltiplos funis"
                            description="Quando ativado, a empresa pode criar mais de um funil de vendas simultaneamente."
                            checked={allowMultiplePipelines}
                            onChange={setAllowMultiplePipelines}
                            disabled={!canUpdate}
                        />

                        <div className="divider my-0" />

                        <ToggleRow
                            label="Permitir lead em múltiplos funis"
                            description="Quando ativado, um mesmo lead pode estar presente em negócios de funis diferentes ao mesmo tempo."
                            checked={allowLeadInMultiplePipelines}
                            onChange={setAllowLeadInMultiplePipelines}
                            disabled={!canUpdate}
                        />

                        <div className="divider my-0" />

                        <div>
                            <div className="flex items-center gap-2">
                                <MdLock className="text-base text-base-content/60" />
                                <p className="font-medium text-base-content">
                                    Perfis que veem todos os funis
                                </p>
                            </div>
                            <p className="text-sm text-base-content/60 mt-0.5 mb-3">
                                Usuários destes perfis veem todos os funis e oportunidades da
                                organização. Administradores sempre veem.
                            </p>
                            <StorageRolePicker
                                selected={viewAllPipelinesRoleIds}
                                onChange={setViewAllPipelinesRoleIds}
                                disabled={!canUpdate || !canUpdatePipelines}
                                emptyMessage="Nenhum perfil de acesso cadastrado. Crie perfis em Segurança > Permissões para liberar a visão de todos os funis."
                                helpText={
                                    canUpdate && !canUpdatePipelines
                                        ? 'Para alterar estes perfis é preciso também permissão de edição de funis.'
                                        : 'Os demais usuários veem só os funis de que são donos e os funis em que o perfil deles está marcado.'
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Distribuição */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body">
                    <div className="flex items-center gap-2 mb-4">
                        <MdAutoAwesome className="text-xl text-secondary" />
                        <h2 className="card-title text-lg">Distribuição por Carteira</h2>
                    </div>

                    <div className="space-y-5">
                        <ToggleRow
                            label="Distribuição automática de leads"
                            description="Novos leads serão automaticamente atribuídos a vendedores com base nas regras de distribuição configuradas em cada carteira."
                            checked={autoAssignLeadWallet}
                            onChange={setAutoAssignLeadWallet}
                            disabled={!canUpdate}
                        />

                        <div className="divider my-0" />

                        <ToggleRow
                            label="Distribuição automática de oportunidades"
                            description="Novas oportunidades (negócios) serão automaticamente atribuídas a vendedores conforme as regras das carteiras."
                            checked={autoAssignDealWallet}
                            onChange={setAutoAssignDealWallet}
                            disabled={!canUpdate}
                        />
                    </div>
                </div>
            </div>

            {/* Análise de Sentimento */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body">
                    <div className="flex items-center gap-2 mb-4">
                        <MdSentimentSatisfied className="text-xl text-accent" />
                        <h2 className="card-title text-lg">Análise de Sentimento</h2>
                    </div>

                    <ToggleRow
                        label="Habilitar análise de sentimento"
                        description="Utiliza inteligência artificial para analisar o sentimento dos leads com base nas interações via chat e WhatsApp."
                        checked={sentimentAnalysisEnabled}
                        onChange={setSentimentAnalysisEnabled}
                        disabled={!canUpdate}
                    />
                </div>
            </div>

            {/* Score Decay */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body">
                    <div className="flex items-center gap-2 mb-4">
                        <MdTrendingDown className="text-xl text-warning" />
                        <h2 className="card-title text-lg">Decaimento de Score</h2>
                    </div>

                    <div className="space-y-5">
                        <ToggleRow
                            label="Habilitar decaimento de score"
                            description="O score dos leads que ficarem inativos por um período será gradualmente reduzido, ajudando a priorizar leads mais engajados."
                            checked={scoreDecayEnabled}
                            onChange={setScoreDecayEnabled}
                            disabled={!canUpdate}
                        />

                        {scoreDecayEnabled && (
                            <>
                                <div className="divider my-0" />
                                <div className="form-control w-full max-w-xs">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Dias para início do decaimento
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        className="input input-bordered w-full"
                                        min={1}
                                        max={365}
                                        value={scoreDecayDays}
                                        onChange={e =>
                                            setScoreDecayDays(
                                                Math.max(1, parseInt(e.target.value) || 1)
                                            )
                                        }
                                        disabled={!canUpdate}
                                    />
                                    <label className="label">
                                        <span className="label-text-alt text-base-content/50">
                                            Após quantos dias sem atividade o score começa a reduzir
                                        </span>
                                    </label>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

function ToggleRow({
    label,
    description,
    checked,
    onChange,
    disabled,
}: {
    label: string
    description: string
    checked: boolean
    onChange: (val: boolean) => void
    disabled?: boolean
}) {
    return (
        <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
                <p className="font-medium text-base-content">{label}</p>
                <p className="text-sm text-base-content/60 mt-0.5">{description}</p>
            </div>
            <label className="cursor-pointer flex-shrink-0">
                <input
                    type="checkbox"
                    className="toggle toggle-secondary"
                    checked={checked}
                    onChange={e => onChange(e.target.checked)}
                    disabled={disabled}
                />
            </label>
        </div>
    )
}
